/**
 * Welcome email renderer.
 *
 * Sent once, immediately after a successful signup. This is NOT a Supabase
 * email-confirmation message — confirmation is currently disabled on this
 * project, so users are fully signed in by the time we trigger this. If
 * email confirmation is ever flipped on, the trigger in lib/auth.ts must
 * be moved to fire after confirmation, not after signup.
 *
 * Personal-founder style: warm, brief, signed by Kris, with a real reply-to.
 * The whole point is to invite a reply — early-stage founder welcomes that
 * get replied to become customer-development conversations.
 *
 * Mirrors the shape of quoteEmail.ts / invoiceEmail.ts:
 *   - render*Email({ ...props }) → { subject, html }
 *   - HTML is a single string with inline styles (email-client safe)
 *   - No external assets, no tracking pixels
 */

export type WelcomeEmailInput = {
  name: string
  workspaceName: string
}

export type WelcomeEmailOutput = {
  subject: string
  html: string
}

export function renderWelcomeEmail({ name, workspaceName }: WelcomeEmailInput): WelcomeEmailOutput {
  // First whitespace-delimited token, matching the convention from quoteEmail.ts.
  const firstName = (name || '').split(' ')[0] || name || 'there'

  const subject = `Welcome to Orkestra, ${firstName}`

  // Email HTML. Tight inline styles, no external CSS, table-free for max
  // compatibility. Tested mentally against Gmail / Apple Mail / Outlook
  // rendering quirks.
  const html = `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>${escape(subject)}</title>
</head>
<body style="margin:0; padding:0; background-color:#f5f5f7; font-family:-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; color:#1a1a1a; line-height:1.6;">
  <div style="max-width:560px; margin:0 auto; padding:40px 24px;">

    <div style="background:#ffffff; border-radius:12px; padding:40px 32px; box-shadow:0 1px 3px rgba(0,0,0,0.04);">

      <p style="margin:0 0 20px; font-size:16px;">Hey ${escape(firstName)},</p>

      <p style="margin:0 0 20px; font-size:16px;">
        Thanks for signing up for Orkestra. I'm Kris — I built it, and I personally read every reply to this email.
      </p>

      <p style="margin:0 0 20px; font-size:16px;">
        Your workspace <strong>${escape(workspaceName)}</strong> is ready to go. You can jump in and start adding contacts, sending quotes, or just clicking around to see what's there.
      </p>

      <p style="margin:0 0 20px; font-size:16px;">
        I'd love to know what you're working on. If you've got a minute, hit reply and tell me — what kind of business is this for, and what made you sign up today? No pressure, no script. I'm trying to make Orkestra genuinely useful for service businesses, and the most useful thing I can do is hear from real people using it.
      </p>

      <p style="margin:0 0 8px; font-size:16px;">Thanks for trying it out.</p>

      <p style="margin:0; font-size:16px;">
        Kris
      </p>

    </div>

    <p style="margin:24px 0 0; font-size:12px; color:#86868b; text-align:center;">
      Sent from Orkestra · You're receiving this because you just signed up.
    </p>

  </div>
</body>
</html>`

  return { subject, html }
}

// Minimal HTML-escape for the few user-controlled values we interpolate
// (firstName, workspaceName, subject). Prevents an innocuous workspace name
// like "Bob's <Best> Plumbing" from breaking layout.
function escape(s: string): string {
  return String(s ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')
}
